import React, { useState, useEffect } from 'react';
import {
  FolderPlus,
  Folder,
  FolderOpen,
  Edit,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Package,
  ChevronDown,
  ChevronsDown,
  ChevronsUp,
  Coins,
} from 'lucide-react';
import styles from './CategorieArticoliPage.module.css';
import categorieArticoliService, { CategoriaArticolo, CategoriaArticoloRequest } from '../../services/categorieArticoliService';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn } from '@/utils/exports';

// Color options
const COLORI = [
  { value: '#1976D2', label: 'Blu' },
  { value: '#4CAF50', label: 'Verde' },
  { value: '#FF9800', label: 'Arancione' },
  { value: '#F44336', label: 'Rosso' },
  { value: '#9C27B0', label: 'Viola' },
  { value: '#00BCD4', label: 'Ciano' },
  { value: '#795548', label: 'Marrone' },
  { value: '#607D8B', label: 'Grigio' },
];

// Formatta il costo di gestione medio della categoria
const formatCostoGestione = (valore?: number | null): string =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(valore ?? 0);

export const CategorieArticoliPage: React.FC = () => {
  // Data states
  const [categorie, setCategorie] = useState<CategoriaArticolo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI states
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoriaArticolo | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CategoriaArticolo | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    colore: '#1976D2',
    costoGestioneMedio: '',
    parentId: '',
    attiva: true,
  });
  const [formErrors, setFormErrors] = useState<{nome?: boolean}>({});
  const [exportLoading, setExportLoading] = useState(false);

  // Load categories on mount
  useEffect(() => {
    loadCategorie();
  }, []);

  // Load categories from backend
  const loadCategorie = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categorieArticoliService.getCategorieArticoli();
      setCategorie(data);
      // Auto-expand all root categories
      const rootCategories = data.filter(c => !c.parentId);
      setExpandedCategories(rootCategories.map(c => c.id));
    } catch (err) {
      setError('Errore nel caricamento delle categorie');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get root categories and organize hierarchy
  const getRootCategories = () => {
    return categorie.filter(c => !c.parentId);
  };

  const getSubcategories = (parentId: number) => {
    return categorie.filter(c => c.parentId === parentId);
  };

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const expandAll = () => {
    const rootCategories = categorie.filter(c => !c.parentId);
    setExpandedCategories(rootCategories.map((c) => c.id));
  };

  const collapseAll = () => {
    setExpandedCategories([]);
  };

  // Open modal for new category
  const openNewCategoryModal = () => {
    setEditingCategory(null);
    setFormData({
      nome: '',
      descrizione: '',
      colore: '#1976D2',
      costoGestioneMedio: '',
      parentId: '',
      attiva: true,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  // Open modal for editing
  const handleEdit = (categoria: CategoriaArticolo) => {
    setEditingCategory(categoria);
    setFormData({
      nome: categoria.nome,
      descrizione: categoria.descrizione || '',
      colore: categoria.colore || '#1976D2',
      costoGestioneMedio: categoria.costoGestioneMedio != null ? String(categoria.costoGestioneMedio) : '',
      parentId: categoria.parentId ? String(categoria.parentId) : '',
      attiva: categoria.isActive,
    });
    setFormErrors({});
    setShowFormModal(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (categoria: CategoriaArticolo) => {
    setDeleteTarget(categoria);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      await categorieArticoliService.deleteCategoriaArticolo(deleteTarget.id);
      await loadCategorie();
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Errore nell\'eliminazione della categoria';
      alert(message);
    }
  };

  // Save category
  const handleSave = async () => {
    // Validate
    const errors: {nome?: boolean} = {};
    if (!formData.nome.trim()) {
      errors.nome = true;
    }
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      const request: CategoriaArticoloRequest = {
        nome: formData.nome.trim(),
        descrizione: formData.descrizione.trim() || undefined,
        colore: formData.colore,
        costoGestioneMedio: formData.costoGestioneMedio ? parseFloat(formData.costoGestioneMedio) : undefined,
        isActive: formData.attiva,
        parentId: formData.parentId ? parseInt(formData.parentId) : undefined,
      };

      if (editingCategory) {
        await categorieArticoliService.updateCategoriaArticolo(editingCategory.id, request);
      } else {
        await categorieArticoliService.createCategoriaArticolo(request);
      }

      await loadCategorie();
      setShowFormModal(false);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Errore nel salvataggio della categoria';
      alert(message);
    }
  };

  // Stats
  const totalCategorie = categorie.length;
  const categorieAttive = categorie.filter(c => c.isActive).length;
  const rootCategories = getRootCategories();

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    if (categorie.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'nome', header: 'Nome', type: 'text', width: 25 },
        { field: 'descrizione', header: 'Descrizione', type: 'text', width: 30 },
        { field: 'colore', header: 'Colore', type: 'text', width: 10 },
        { field: 'costoGestioneMedio', header: 'Costo Gestione Medio', type: 'currency', width: 18 },
        { field: 'parentNome', header: 'Categoria Padre', type: 'text', width: 20 },
        { field: 'articoliCount', header: 'N. Articoli', type: 'number', width: 12 },
      ];

      // Build flat list with indentation for subcategories
      const exportData = categorie.map(cat => {
        const parent = cat.parentId ? categorie.find(c => c.id === cat.parentId) : null;
        const isSubcategory = !!cat.parentId;
        const colorLabel = COLORI.find(c => c.value === cat.colore)?.label || cat.colore || '';
        return {
          nome: isSubcategory ? `  └ ${cat.nome}` : cat.nome,
          descrizione: cat.descrizione || '',
          colore: colorLabel,
          costoGestioneMedio: cat.costoGestioneMedio ?? 0,
          parentNome: parent?.nome || '',
          articoliCount: cat.articoliCount || 0,
        };
      });

      const config = {
        title: 'Categorie Articoli',
        filename: 'categorie_articoli',
        columns,
        orientation: 'portrait' as const,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting categorie articoli:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageTitle}>
          <Package size={28} />
          <h1>Merceologia</h1>
        </div>
        <div className={styles.pageActions}>
          <ExportDropdown
            onExport={handleExport}
            disabled={categorie.length === 0}
            loading={exportLoading}
          />
          <button className={styles.btnPrimary} onClick={openNewCategoryModal}>
            <FolderPlus size={18} />
            Nuova Categoria
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Totale Categorie</div>
          <div className={styles.statValue}>{totalCategorie}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Categorie Attive</div>
          <div className={styles.statValue}>{categorieAttive}</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statLabel}>Categorie Principali</div>
          <div className={styles.statValue}>{rootCategories.length}</div>
        </div>
      </div>

      {/* Categories Card */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>
            <Folder size={20} />
            Elenco Categorie
          </h3>
          <div className={styles.headerActions}>
            <button className={styles.btnIcon} onClick={expandAll} title="Espandi tutte">
              <ChevronsDown size={18} />
            </button>
            <button className={styles.btnIcon} onClick={collapseAll} title="Comprimi tutte">
              <ChevronsUp size={18} />
            </button>
          </div>
        </div>

        <div className={styles.cardBody}>
          {loading && <div className={styles.loading}>Caricamento...</div>}
          {error && <div className={styles.error}>{error}</div>}

          {!loading && !error && rootCategories.length === 0 && (
            <div className={styles.emptyState}>
              <Folder size={48} />
              <p>Nessuna categoria presente</p>
              <button className={styles.btnPrimary} onClick={openNewCategoryModal}>
                <FolderPlus size={18} />
                Crea la prima categoria
              </button>
            </div>
          )}

          {!loading && !error && rootCategories.length > 0 && (
            <div className={styles.categoriesList}>
              {rootCategories.map((categoria) => {
                const subcategories = getSubcategories(categoria.id);
                const isExpanded = expandedCategories.includes(categoria.id);

                return (
                  <div key={categoria.id} className={styles.categoriaItem}>
                    <div
                      className={styles.categoriaHeader}
                      onClick={() => subcategories.length > 0 && toggleCategory(categoria.id)}
                    >
                      <div className={styles.categoriaLeft}>
                        {subcategories.length > 0 ? (
                          <ChevronDown
                            size={18}
                            className={`${styles.chevron} ${isExpanded ? styles.expanded : ''}`}
                          />
                        ) : (
                          <div style={{ width: 18 }} />
                        )}
                        <div
                          className={styles.categoriaColor}
                          style={{ backgroundColor: categoria.colore || '#1976D2' }}
                        />
                        {isExpanded ? <FolderOpen size={20} /> : <Folder size={20} />}
                        <span className={styles.categoriaNome}>{categoria.nome}</span>
                        {!categoria.isActive && (
                          <span className={styles.badgeInactive}>Inattiva</span>
                        )}
                      </div>
                      <div className={styles.categoriaRight}>
                        <div className={styles.badgeGroup}>
                          {subcategories.length > 0 && (
                            <span className={styles.countBadge}>
                              {subcategories.length} sotto
                            </span>
                          )}
                          <span className={styles.countBadge}>
                            {categoria.articoliCount || 0} articoli
                          </span>
                          <span
                            className={`${styles.costBadge} ${categoria.costoGestioneMedio == null ? styles.costBadgeEmpty : ''}`}
                            title="Costo di gestione medio per la vendita di un articolo di questa categoria"
                          >
                            <Coins size={12} />
                            Gestione {categoria.costoGestioneMedio != null ? formatCostoGestione(categoria.costoGestioneMedio) : '—'}
                          </span>
                        </div>
                        <button
                          className={styles.btnIcon}
                          onClick={(e) => { e.stopPropagation(); handleEdit(categoria); }}
                          title="Modifica"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          className={styles.btnIconDanger}
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(categoria); }}
                          title="Elimina"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    {isExpanded && subcategories.length > 0 && (
                      <div className={styles.subcategoriesList}>
                        {subcategories.map((sub) => (
                          <div key={sub.id} className={styles.subcategoriaItem}>
                            <div className={styles.categoriaLeft}>
                              <div style={{ width: 18 }} />
                              <div
                                className={styles.categoriaColor}
                                style={{ backgroundColor: sub.colore || categoria.colore || '#1976D2' }}
                              />
                              <Folder size={16} />
                              <span className={styles.categoriaNome}>{sub.nome}</span>
                              {!sub.isActive && (
                                <span className={styles.badgeInactive}>Inattiva</span>
                              )}
                            </div>
                            <div className={styles.categoriaRight}>
                              <div className={styles.badgeGroup}>
                                <span className={styles.countBadge}>
                                  {sub.articoliCount || 0} articoli
                                </span>
                                <span
                                  className={`${styles.costBadge} ${sub.costoGestioneMedio == null ? styles.costBadgeEmpty : ''}`}
                                  title="Costo di gestione medio per la vendita di un articolo di questa categoria"
                                >
                                  <Coins size={12} />
                                  Gestione {sub.costoGestioneMedio != null ? formatCostoGestione(sub.costoGestioneMedio) : '—'}
                                </span>
                              </div>
                              <button
                                className={styles.btnIcon}
                                onClick={() => handleEdit(sub)}
                                title="Modifica"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                className={styles.btnIconDanger}
                                onClick={() => handleDeleteClick(sub)}
                                title="Elimina"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFormModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>
                <FolderPlus size={24} />
                {editingCategory ? 'Modifica Categoria' : 'Nuova Categoria'}
              </h3>
              <button className={styles.modalClose} onClick={() => setShowFormModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles.modalBody}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Nome *</label>
                <input
                  type="text"
                  className={`${styles.formControl} ${formErrors.nome ? styles.error : ''}`}
                  value={formData.nome}
                  onChange={(e) => {
                    setFormData({ ...formData, nome: e.target.value });
                    setFormErrors({ ...formErrors, nome: false });
                  }}
                  placeholder="Nome categoria..."
                />
                {formErrors.nome && <span className={styles.errorText}>Campo obbligatorio</span>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Descrizione</label>
                <textarea
                  className={styles.formControl}
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  placeholder="Descrizione opzionale..."
                  rows={2}
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Costo gestione medio (€)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  className={styles.formControl}
                  value={formData.costoGestioneMedio}
                  onChange={(e) => setFormData({ ...formData, costoGestioneMedio: e.target.value })}
                  placeholder="0.00"
                />
                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  Costo medio di gestione per la vendita di un articolo di questa categoria. Usato per stimare il ricavo netto.
                </span>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Colore</label>
                  <select
                    className={styles.formControl}
                    value={formData.colore}
                    onChange={(e) => setFormData({ ...formData, colore: e.target.value })}
                  >
                    {COLORI.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Categoria Padre</label>
                  <select
                    className={styles.formControl}
                    value={formData.parentId}
                    onChange={(e) => setFormData({ ...formData, parentId: e.target.value })}
                    disabled={!!(editingCategory && getSubcategories(editingCategory.id).length > 0)}
                  >
                    <option value="">-- Nessuna (principale) --</option>
                    {getRootCategories()
                      .filter(c => c.id !== editingCategory?.id)
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))
                    }
                  </select>
                </div>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxWrapper}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={formData.attiva}
                    onChange={(e) => setFormData({ ...formData, attiva: e.target.checked })}
                  />
                  <span>Categoria attiva</span>
                </label>
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setShowFormModal(false)}>
                <X size={16} />
                Annulla
              </button>
              <button className={styles.btnPrimary} onClick={handleSave}>
                <Save size={16} />
                Salva
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={40} />
            </div>
            <h3>Eliminare questa categoria?</h3>
            <p>
              Stai per eliminare la categoria <strong>{deleteTarget.nome}</strong>.
              {(deleteTarget.articoliCount || 0) > 0 && (
                <> Contiene <strong>{deleteTarget.articoliCount} articoli</strong>.</>
              )}
              <br />
              Questa azione non può essere annullata.
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleConfirmDelete}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategorieArticoliPage;
